import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Assignment as InitiativeIcon,
  Delete as DeleteIcon,
  Link as LinkIcon,
} from '@mui/icons-material';
import { Initiative } from '../../types/database';
import { useDatabase } from '../../hooks/useDatabase';

interface InitiativeRelationshipsProps {
  initiative: Initiative;
  onUpdate: (updatedInitiative: Initiative) => void;
}

const RELATIONSHIP_TYPES = [
  'Parent',
  'Child',
  'Related',
  'Dependency',
  'Successor',
  'Predecessor',
  'Other',
];

export const InitiativeRelationships: React.FC<InitiativeRelationshipsProps> = ({
  initiative,
  onUpdate,
}) => {
  const db = useDatabase();
  const navigate = useNavigate();
  const [allInitiatives, setAllInitiatives] = useState<Initiative[]>([]);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [selectedInitiative, setSelectedInitiative] = useState<Initiative | null>(null);
  const [relationshipType, setRelationshipType] = useState<string>('Related');

  useEffect(() => {
    const loadInitiatives = async () => {
      const { data } = await db.getInitiatives();
      setAllInitiatives(data || []);
    };
    loadInitiatives();
  }, [db]);

  const handleLinkInitiative = async () => {
    if (!selectedInitiative) return;

    try {
      // Update the current initiative
      const updatedRelatedInitiatives = [...initiative.related_initiatives, selectedInitiative.id];
      const updatedRelationshipTypes = {
        ...initiative.relationship_types,
        [selectedInitiative.id]: relationshipType,
      };

      const { data: updatedInitiative, error: error1 } = await db.updateInitiative(initiative.id, {
        related_initiatives: updatedRelatedInitiatives,
        relationship_types: updatedRelationshipTypes,
      });

      if (error1) throw error1;

      // Update the selected initiative
      const selectedInitiativeUpdated = {
        ...selectedInitiative,
        related_initiatives: [...selectedInitiative.related_initiatives, initiative.id],
        relationship_types: {
          ...selectedInitiative.relationship_types,
          [initiative.id]: relationshipType,
        },
      };

      const { error: error2 } = await db.updateInitiative(selectedInitiative.id, {
        related_initiatives: selectedInitiativeUpdated.related_initiatives,
        relationship_types: selectedInitiativeUpdated.relationship_types,
      });

      if (error2) throw error2;

      if (updatedInitiative) {
        onUpdate(updatedInitiative);
      }

      setLinkDialogOpen(false);
      setSelectedInitiative(null);
      setRelationshipType('Related');
    } catch (err) {
      console.error('Failed to link initiative:', err);
    }
  };

  const handleUnlinkInitiative = async (initiativeId: string) => {
    try {
      // Update the current initiative
      const updatedRelatedInitiatives = initiative.related_initiatives.filter(id => id !== initiativeId);
      const updatedRelationshipTypes = { ...initiative.relationship_types };
      delete updatedRelationshipTypes[initiativeId];

      const { data: updatedInitiative, error: error1 } = await db.updateInitiative(initiative.id, {
        related_initiatives: updatedRelatedInitiatives,
        relationship_types: updatedRelationshipTypes,
      });

      if (error1) throw error1;

      // Update the other initiative
      const otherInitiative = allInitiatives.find(i => i.id === initiativeId);
      if (otherInitiative) {
        const otherUpdatedRelatedInitiatives = otherInitiative.related_initiatives.filter(id => id !== initiative.id);
        const otherUpdatedRelationshipTypes = { ...otherInitiative.relationship_types };
        delete otherUpdatedRelationshipTypes[initiative.id];

        const { error: error2 } = await db.updateInitiative(initiativeId, {
          related_initiatives: otherUpdatedRelatedInitiatives,
          relationship_types: otherUpdatedRelationshipTypes,
        });

        if (error2) throw error2;
      }

      if (updatedInitiative) {
        onUpdate(updatedInitiative);
      }
    } catch (err) {
      console.error('Failed to unlink initiative:', err);
    }
  };

  const getRelatedInitiatives = () => {
    return allInitiatives.filter(i => initiative.related_initiatives.includes(i.id));
  };

  return (
    <Paper sx={{ p: 3, height: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Related Initiatives</Typography>
        <Button
          startIcon={<LinkIcon />}
          onClick={() => setLinkDialogOpen(true)}
          disabled={allInitiatives.length === 0}
        >
          Link Initiative
        </Button>
      </Box>

      {getRelatedInitiatives().length > 0 ? (
        <List>
          {getRelatedInitiatives().map((relatedInitiative) => (
            <ListItem
              key={relatedInitiative.id}
              secondaryAction={
                <IconButton
                  edge="end"
                  onClick={() => handleUnlinkInitiative(relatedInitiative.id)}
                >
                  <DeleteIcon />
                </IconButton>
              }
              sx={{ 
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'action.hover'
                }
              }}
              onClick={() => navigate(`/initiatives/${relatedInitiative.id}`)}
            >
              <ListItemIcon>
                <InitiativeIcon />
              </ListItemIcon>
              <ListItemText
                primary={relatedInitiative.name}
                secondary={initiative.relationship_types[relatedInitiative.id] || 'No relationship type'}
              />
            </ListItem>
          ))}
        </List>
      ) : (
        <Typography variant="body2" color="text.secondary">
          No related initiatives
        </Typography>
      )}

      <Dialog
        open={linkDialogOpen}
        onClose={() => {
          setLinkDialogOpen(false);
          setSelectedInitiative(null);
          setRelationshipType('Related');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Link Initiative</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Autocomplete
              options={allInitiatives.filter(i => i.id !== initiative.id)}
              getOptionLabel={(option) => option.name}
              value={selectedInitiative}
              onChange={(_, newValue) => setSelectedInitiative(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Initiative"
                  fullWidth
                />
              )}
            />
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Relationship Type (not required)</InputLabel>
              <Select
                value={relationshipType}
                onChange={(e) => setRelationshipType(e.target.value)}
                label="Relationship Type (not required)"
              >
                {RELATIONSHIP_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setLinkDialogOpen(false);
              setSelectedInitiative(null);
              setRelationshipType('Related');
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleLinkInitiative}
            variant="contained"
            disabled={!selectedInitiative}
          >
            Link Initiative
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}; 